/**
 * ImageUploadExtension - 邮件编辑器图片上传扩展
 *
 * 功能：
 * 1. 支持粘贴图片（从剪贴板）自动上传到 OSS 并插入编辑器
 * 2. 支持拖拽图片文件到编辑器区域上传
 * 3. 支持工具栏按钮选择文件上传
 * 4. 自动压缩大图片（>200KB 的 PNG 转 JPEG，仿 Gmail/Outlook 做法）
 * 5. 存储 object_key 到 data-object-key 属性，供发送时 CID 内嵌使用
 * 6. 编辑器预览阶段使用 OSS 签名 URL 显示图片
 *
 * 防护机制：
 * - 单张图片原始上限 10MB，压缩后上传
 * - 总内嵌图片体积上限 15MB（Base64 后约 20MB，低于主流 SMTP 的 25MB 限制）
 * - 格式白名单：仅 JPEG/PNG/GIF（邮件客户端兼容性最好）
 * - 上传失败自动清理占位图
 * - blob URL 残留在 cleanHtmlForEmail 中兜底过滤
 *
 * 数据流：
 *   用户粘贴/拖拽/选择图片
 *     → validateAndCompress(file)  // 格式校验 + 自动压缩
 *     → uploadFile(compressedFile) 上传到 OSS
 *     → getPresignedViewUrl(object_key) 获取签名 URL
 *     → editor.setImage({ src: signedUrl, data-object-key: objectKey })
 *     → 发送时后端根据 data-object-key 做 CID 内嵌
 */
import Image from "@tiptap/extension-image";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { uploadFile } from "../../../api/attachmentApi";
import { attachmentApi } from "../../../api/attachmentApi";
import { message } from "antd";

const IMAGE_UPLOAD_PLUGIN_KEY = new PluginKey("imageUpload");

// ==================== 常量配置 ====================

/** 单张图片原始大小上限（压缩前校验）*/
const MAX_SINGLE_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB

/** 压缩阈值：超过此大小的图片会被自动压缩 */
const COMPRESS_THRESHOLD = 200 * 1024; // 200KB

/** 压缩目标最大宽度（像素）：邮件正文通常不需要超大分辨率 */
const COMPRESS_MAX_WIDTH = 1600;

/** 压缩质量（JPEG quality 参数）*/
const COMPRESS_QUALITY = 0.85;

/** 总内嵌图片体积上限（所有图片压缩后的累计体积）*/
const MAX_TOTAL_INLINE_SIZE = 15 * 1024 * 1024; // 15MB

/** 邮件兼容的图片格式白名单 */
const ALLOWED_IMAGE_TYPES = new Set([
    "image/jpeg",
    "image/png",
    "image/gif",
]);

/** 追踪当前编辑器中已嵌入图片的累计体积（模块级状态） */
let currentTotalImageSize = 0;

// ==================== 图片压缩 ====================

/**
 * 使用 Canvas 压缩图片
 *
 * 策略（仿 Gmail/Outlook 网页版）：
 * - PNG 截图通常体积巨大（未压缩），转为 JPEG 可缩小 5-10 倍
 * - GIF 保持原格式（可能包含动画）
 * - 已经是 JPEG 的如果超过阈值也会重新压缩（降低质量/分辨率）
 * - 压缩后如果比原图更大则使用原图（罕见但理论可能）
 *
 * @param {File} file - 原始图片文件
 * @returns {Promise<File>} - 压缩后的文件（或原文件如果无需压缩）
 */
async function compressImage(file) {
    // GIF 保持原格式（可能包含动画，Canvas 会丢失帧）
    if (file.type === "image/gif") {
        return file;
    }

    // 小于阈值的图片不压缩
    if (file.size <= COMPRESS_THRESHOLD) {
        return file;
    }

    return new Promise((resolve) => {
        const img = new window.Image();
        const objectUrl = URL.createObjectURL(file);

        img.onload = () => {
            URL.revokeObjectURL(objectUrl);

            // 计算目标尺寸（保持宽高比，限制最大宽度）
            let { width, height } = img;
            if (width > COMPRESS_MAX_WIDTH) {
                height = Math.round((height * COMPRESS_MAX_WIDTH) / width);
                width = COMPRESS_MAX_WIDTH;
            }

            // Canvas 压缩
            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0, width, height);

            canvas.toBlob(
                (blob) => {
                    if (!blob) {
                        console.warn("[ImageUploadExtension] Canvas 压缩失败，使用原图");
                        resolve(file);
                        return;
                    }

                    // 如果压缩后反而更大，使用原图
                    if (blob.size >= file.size) {
                        console.log(
                            `[ImageUploadExtension] 压缩后 (${(blob.size / 1024).toFixed(0)}KB) >= 原图 (${(file.size / 1024).toFixed(0)}KB)，保留原图`
                        );
                        resolve(file);
                        return;
                    }

                    // 构造压缩后的 File 对象，保留原始文件名（扩展名改为 .jpg）
                    const compressedName = file.name.replace(/\.\w+$/, ".jpg");
                    const compressedFile = new File([blob], compressedName, {
                        type: "image/jpeg",
                        lastModified: Date.now(),
                    });

                    console.log(
                        `[ImageUploadExtension] 图片压缩: ${(file.size / 1024).toFixed(0)}KB → ${(compressedFile.size / 1024).toFixed(0)}KB ` +
                        `(${Math.round((1 - compressedFile.size / file.size) * 100)}% 压缩率, ${width}×${height})`
                    );

                    resolve(compressedFile);
                },
                "image/jpeg",
                COMPRESS_QUALITY
            );
        };

        img.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            console.warn("[ImageUploadExtension] 图片加载失败，使用原图上传");
            resolve(file);
        };

        img.src = objectUrl;
    });
}

// ==================== 核心上传逻辑 ====================

/**
 * 处理图片文件上传的核心函数
 * @param {File} file - 图片文件
 * @param {Editor} editor - Tiptap 编辑器实例
 * @param {number|null} pos - 插入位置（null 则使用当前光标位置）
 */
async function handleImageUpload(file, editor, pos = null) {
    // 1. 格式白名单校验
    if (!ALLOWED_IMAGE_TYPES.has(file.type)) {
        const allowed = Array.from(ALLOWED_IMAGE_TYPES)
            .map((t) => t.replace("image/", "").toUpperCase())
            .join("、");
        message.warning(`仅支持 ${allowed} 格式的图片，当前格式: ${file.type}`);
        return false;
    }

    // 2. 单张图片原始大小校验（压缩前）
    if (file.size > MAX_SINGLE_IMAGE_SIZE) {
        message.error(`图片过大（${(file.size / 1024 / 1024).toFixed(1)}MB），单张上限 ${MAX_SINGLE_IMAGE_SIZE / 1024 / 1024}MB`);
        return false;
    }

    // 3. 自动压缩
    const processedFile = await compressImage(file);

    // 4. 总体积校验（压缩后的实际体积）
    if (currentTotalImageSize + processedFile.size > MAX_TOTAL_INLINE_SIZE) {
        const currentMB = (currentTotalImageSize / 1024 / 1024).toFixed(1);
        const limitMB = (MAX_TOTAL_INLINE_SIZE / 1024 / 1024).toFixed(0);
        message.error(`内嵌图片总体积已达 ${currentMB}MB，超过上限 ${limitMB}MB。请减少图片数量或使用附件发送大文件。`);
        return false;
    }

    // 5. 先用本地 blob URL 创建占位图片，提供即时反馈
    const placeholderUrl = URL.createObjectURL(processedFile);
    const placeholderAttrs = {
        src: placeholderUrl,
        alt: file.name,
        title: file.name,
        "data-object-key": "",
        "data-uploading": "true",
    };

    // 在指定位置或光标位置插入占位图片
    if (pos !== null) {
        editor.chain().focus().insertContentAt(pos, {
            type: "image",
            attrs: placeholderAttrs,
        }).run();
    } else {
        editor.chain().focus().setImage(placeholderAttrs).run();
    }

    try {
        // 6. 上传到 OSS（使用压缩后的文件）
        const result = await uploadFile(processedFile, () => {}, false);
        const objectKey = result.storage_path;

        // 7. 获取签名 URL 用于预览（有效期 1 小时）
        const viewData = await attachmentApi.getPresignedViewUrl(objectKey, 3600);
        const signedUrl = viewData.signed_url;

        // 8. 查找并替换占位图片节点
        const { state, dispatch } = editor.view;
        const { tr } = state;
        let found = false;

        state.doc.descendants((node, nodePos) => {
            if (
                !found &&
                node.type.name === "image" &&
                node.attrs.src === placeholderUrl &&
                node.attrs["data-uploading"] === "true"
            ) {
                tr.setNodeMarkup(nodePos, null, {
                    ...node.attrs,
                    src: signedUrl,
                    "data-object-key": objectKey,
                    "data-uploading": null,
                });
                found = true;
            }
        });

        if (found) {
            dispatch(tr);
        }

        // 9. 更新累计体积
        currentTotalImageSize += processedFile.size;

        // 释放 blob URL
        URL.revokeObjectURL(placeholderUrl);

        console.log(
            `[ImageUploadExtension] 图片上传成功: ${file.name} → ${objectKey} ` +
            `(${(processedFile.size / 1024).toFixed(0)}KB, 累计 ${(currentTotalImageSize / 1024 / 1024).toFixed(1)}MB)`
        );

        return true;
    } catch (error) {
        console.error("[ImageUploadExtension] 图片上传失败:", error);
        message.error(`图片上传失败: ${error.message}`);

        // 上传失败：移除占位图片
        const { state, dispatch } = editor.view;
        const { tr } = state;
        let found = false;

        state.doc.descendants((node, nodePos) => {
            if (
                !found &&
                node.type.name === "image" &&
                node.attrs.src === placeholderUrl
            ) {
                tr.delete(nodePos, nodePos + node.nodeSize);
                found = true;
            }
        });

        if (found) {
            dispatch(tr);
        }

        URL.revokeObjectURL(placeholderUrl);
        return false;
    }
}

/**
 * 从事件中提取图片文件列表
 */
function extractImageFiles(dataTransfer) {
    const files = [];
    if (dataTransfer?.files) {
        for (const file of dataTransfer.files) {
            if (file.type.startsWith("image/")) {
                files.push(file);
            }
        }
    }
    // 也检查 items（用于粘贴场景）
    if (files.length === 0 && dataTransfer?.items) {
        for (const item of dataTransfer.items) {
            if (item.type.startsWith("image/") && item.kind === "file") {
                const file = item.getAsFile();
                if (file) {
                    files.push(file);
                }
            }
        }
    }
    return files;
}

// ==================== Tiptap Extension ====================

/**
 * 扩展 Tiptap Image 的自定义图片上传扩展
 */
const ImageUploadExtension = Image.extend({
    name: "image",

    addAttributes() {
        return {
            ...this.parent?.(),
            // 存储 OSS 对象键，用于发送时 CID 内嵌
            "data-object-key": {
                default: null,
                parseHTML: (element) => element.getAttribute("data-object-key"),
                renderHTML: (attributes) => {
                    if (!attributes["data-object-key"]) return {};
                    return { "data-object-key": attributes["data-object-key"] };
                },
            },
            // 上传中状态标记
            "data-uploading": {
                default: null,
                parseHTML: (element) => element.getAttribute("data-uploading"),
                renderHTML: (attributes) => {
                    if (!attributes["data-uploading"]) return {};
                    return { "data-uploading": attributes["data-uploading"] };
                },
            },
        };
    },

    addProseMirrorPlugins() {
        const editor = this.editor;

        return [
            ...(this.parent?.() ?? []),
            new Plugin({
                key: IMAGE_UPLOAD_PLUGIN_KEY,
                props: {
                    /**
                     * 处理粘贴事件 - 拦截剪贴板中的图片
                     */
                    handlePaste(view, event) {
                        const imageFiles = extractImageFiles(event.clipboardData);

                        if (imageFiles.length === 0) {
                            return false; // 不是图片粘贴，交给默认处理
                        }

                        // 阻止默认粘贴行为
                        event.preventDefault();

                        // 逐个上传图片
                        imageFiles.forEach((file) => {
                            handleImageUpload(file, editor);
                        });

                        return true;
                    },

                    /**
                     * 处理拖拽放下事件 - 拦截拖入的图片文件
                     */
                    handleDrop(view, event) {
                        const imageFiles = extractImageFiles(event.dataTransfer);

                        if (imageFiles.length === 0) {
                            return false;
                        }

                        event.preventDefault();

                        // 计算拖拽放下的位置
                        const coordinates = view.posAtCoords({
                            left: event.clientX,
                            top: event.clientY,
                        });

                        const pos = coordinates ? coordinates.pos : null;

                        imageFiles.forEach((file) => {
                            handleImageUpload(file, editor, pos);
                        });

                        return true;
                    },
                },
            }),
        ];
    },
});

// ==================== 公开 API ====================

/**
 * 通过文件选择器插入图片（工具栏按钮调用）
 * @param {Editor} editor
 */
export function insertImageViaFilePicker(editor) {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/jpeg,image/png,image/gif"; // 与白名单保持一致
    input.multiple = true;

    input.onchange = (e) => {
        const files = Array.from(e.target.files);
        files.forEach((file) => {
            handleImageUpload(file, editor);
        });
    };

    input.click();
}

/**
 * 重置图片体积追踪计数器
 * 在编辑器关闭/重新打开时调用，避免跨会话累积
 */
export function resetImageSizeTracker() {
    currentTotalImageSize = 0;
}

export default ImageUploadExtension;
